import { Sample } from '../../@types/samples/types';
import { AnalysisMethod, SampleRunStatus } from './types';

/**
 * Validation heuristics for batch Liao results. Thresholds are named
 * constants so they can be tuned with domain feedback. All checks work
 * off the `calculate_sample` response alone (no worker needed).
 */

/** RMSE of the dimensionless PcD fit above which the fit is questionable. */
export const LIAO_RMSE_WARNING_THRESHOLD = 0.05;
/** Fewer valid fit points than this makes the fit low-confidence. */
export const LIAO_MIN_FIT_POINTS = 5;
/** Brooks-Corey ln-ln regression R² below which the fit is questionable. */
export const LAMBDA_R2_WARNING_THRESHOLD = 0.9;
/** Fewer regression points than this makes the Brooks-Corey fit weak. */
export const LAMBDA_MIN_FIT_POINTS = 3;
/** Displayed for an inferred Swi — informational, does NOT downgrade status. */
export const LAMBDA_SWI_INFERRED_REASON =
  'Swi missing — swir inferred from the minimum measured Sw';

/** Whether a reason string is the (informational) inferred-Swi note. */
const isSwiInferredReason = (reason: string): boolean =>
  reason.toLowerCase().includes('swir inferred');
/** Optimizer bounds used by the batch run (mirrors `findBestValues`). */
export const LIAO_DEFAULT_BOUNDS: [number, number][] = [
  [0.000001, 1],
  [1, 1000],
];
const [A_LOWER, A_UPPER] = LIAO_DEFAULT_BOUNDS[0];
const [B_LOWER, B_UPPER] = LIAO_DEFAULT_BOUNDS[1];
/** Relative proximity to a bound that counts as "stuck at the bound". */
const BOUND_PROXIMITY = 0.001;

export interface DerivedRunStatus {
  status: Extract<SampleRunStatus, 'valid' | 'warning' | 'failed'>;
  reasons: string[];
}

const atUpperBound = (value: number, bound: number) =>
  value >= bound * (1 - BOUND_PROXIMITY);

const atLowerBound = (value: number, bound: number) =>
  value <= bound * (1 + BOUND_PROXIMITY);

/** Derive the validation status of a sample after a batch run. */
export const deriveRunStatus = (
  sample: Sample,
  method: AnalysisMethod = 'liao'
): DerivedRunStatus => {
  if (method === 'lambda') return deriveLambdaRunStatus(sample);
  if (method === 'water-oil' || method === 'gas-oil') {
    return deriveKrRunStatus(sample, method);
  }
  return deriveLiaoRunStatus(sample);
};

/**
 * Kr (water-oil / gas-oil): the backend outcome is authoritative — its
 * `analysis_outcomes[method]` carries the skip reason on failure and, on
 * success, the refinement quality warnings (exponent-sum band, borderline
 * regressions, Craig's-rule crossover, preserved manual refinement, …).
 * Any warning ⇒ Review.
 */
const deriveKrRunStatus = (
  sample: Sample,
  method: 'water-oil' | 'gas-oil'
): DerivedRunStatus => {
  const outcome = sample.analysis_outcomes?.[method];
  const values =
    method === 'water-oil'
      ? sample.k_rel_values?.water_oil
      : sample.k_rel_values?.gas_oil;
  const exponent =
    method === 'water-oil'
      ? (values as { no_w?: number | null } | null | undefined)?.no_w
      : (values as { no_g?: number | null } | null | undefined)?.no_g;

  if (outcome && outcome.status === 'failed') {
    return {
      status: 'failed',
      reasons: [outcome.reason || 'Kr calculation failed'],
    };
  }

  if (!values || exponent == null) {
    return {
      status: 'failed',
      reasons: ['No Kr results returned by the calculation'],
    };
  }

  const reasons = outcome?.warnings ? [...outcome.warnings] : [];
  return reasons.length > 0
    ? { status: 'warning', reasons }
    : { status: 'valid', reasons: [] };
};

/** Liao: backend outcome (skip reason, fit RMSE) + optimizer-bound heuristics. */
const deriveLiaoRunStatus = (sample: Sample): DerivedRunStatus => {
  const outcome = sample.analysis_outcomes?.liao;
  const liao = sample.analysis_values?.liao;

  if (outcome && outcome.status === 'failed') {
    return {
      status: 'failed',
      reasons: [outcome.reason || 'Liao calculation failed'],
    };
  }

  if (!liao || liao.best_a == null || liao.best_b == null) {
    return {
      status: 'failed',
      reasons: ['No Liao results returned by the calculation'],
    };
  }

  const reasons: string[] = outcome?.warnings ? [...outcome.warnings] : [];

  if (
    outcome?.fit_rmse != null &&
    outcome.fit_rmse > LIAO_RMSE_WARNING_THRESHOLD
  ) {
    reasons.push(
      `High fit error (RMSE ${outcome.fit_rmse.toFixed(4)} > ${LIAO_RMSE_WARNING_THRESHOLD})`
    );
  }

  if (
    outcome?.n_fit_points != null &&
    outcome.n_fit_points < LIAO_MIN_FIT_POINTS
  ) {
    reasons.push(
      `Few valid fit points (${outcome.n_fit_points} < ${LIAO_MIN_FIT_POINTS})`
    );
  }

  if (atUpperBound(liao.best_b, B_UPPER) || atLowerBound(liao.best_b, B_LOWER)) {
    reasons.push(`'b' stuck at optimizer bound (${liao.best_b})`);
  }

  if (atUpperBound(liao.best_a, A_UPPER) || atLowerBound(liao.best_a, A_LOWER)) {
    reasons.push(`'a' stuck at optimizer bound (${liao.best_a})`);
  }

  if (liao.swir == null) {
    reasons.push('Missing Swir in the Liao results');
  }

  if (liao.fzi == null) {
    reasons.push('Missing FZI — correlation analyses will skip this sample');
  }

  if (liao.pce == null || liao.pce <= 0) {
    reasons.push(`Non-positive Pce (${liao.pce ?? 'missing'})`);
  }

  if (reasons.length > 0) {
    return { status: 'warning', reasons };
  }

  return { status: 'valid', reasons: [] };
};

/**
 * Brooks-Corey: the backend is authoritative — its `analysis_outcomes.lambda`
 * carries the skip reason on failure and, on success, the automatic
 * point-selection warnings (borderline R², fallback point count, inferred
 * Swi, …). The frontend surfaces those and adds only sanity checks.
 *
 * Sign convention (verified against the single-sample implementation): the
 * app's lambda is NEGATIVE for normal drainage data — the curve model is
 * pc = pe·swd^λ with λ<0 so Pc rises toward swir. A non-negative λ is the
 * abnormal case.
 */
const deriveLambdaRunStatus = (sample: Sample): DerivedRunStatus => {
  const outcome = sample.analysis_outcomes?.lambda;
  const lambda = sample.analysis_values?.lambda_values;

  if (outcome && outcome.status === 'failed') {
    return {
      status: 'failed',
      reasons: [outcome.reason || 'Brooks-Corey calculation failed'],
    };
  }

  if (!lambda || lambda.pe == null || lambda.lambda_value == null) {
    return {
      status: 'failed',
      reasons: ['No Brooks-Corey results returned by the calculation'],
    };
  }

  // Downgrading reasons force a Review status; the inferred-Swi note is
  // informational (shown but keeps the sample Valid).
  const reasons: string[] = [];
  let swiInferred = Boolean(outcome?.swi_inferred);

  if (outcome?.warnings?.length) {
    // Backend selection warnings are the primary review reasons; the Swi
    // note (present in older responses) is pulled out as informational.
    outcome.warnings.forEach(warning => {
      if (isSwiInferredReason(warning)) {
        swiInferred = true;
      } else {
        reasons.push(warning);
      }
    });
  } else if (
    // Legacy fallback for responses without backend warnings.
    outcome?.r_squared != null &&
    outcome.r_squared < LAMBDA_R2_WARNING_THRESHOLD
  ) {
    reasons.push(
      `Weak regression fit (R² ${outcome.r_squared.toFixed(4)} < ${LAMBDA_R2_WARNING_THRESHOLD})`
    );
  }

  if (
    outcome?.n_fit_points != null &&
    outcome.n_fit_points < LAMBDA_MIN_FIT_POINTS
  ) {
    reasons.push(
      `Few regression points (${outcome.n_fit_points} < ${LAMBDA_MIN_FIT_POINTS})`
    );
  }

  if (lambda.pe <= 0) {
    reasons.push(`Non-positive Pe (${lambda.pe})`);
  }

  if (lambda.lambda_value >= 0) {
    reasons.push(
      `Unexpected lambda sign (${lambda.lambda_value} — expected negative)`
    );
  }

  const status = reasons.length > 0 ? 'warning' : 'valid';
  // Keep the Swi note visible in both Valid and Review states.
  const displayReasons = swiInferred
    ? [...reasons, LAMBDA_SWI_INFERRED_REASON]
    : reasons;

  return { status, reasons: displayReasons };
};

/** Derive the status for a request that failed before producing a sample. */
export const deriveRunFailure = (errorMessage?: string): DerivedRunStatus => ({
  status: 'failed',
  reasons: [errorMessage || 'Calculation request failed'],
});
